import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { VaSearchInput } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

/**
 * What the search matches. va-search-input renders its `label` for screen
 * readers only, so the same words also show above the box, or a sighted
 * reader who tries a form number sees no reason it finds nothing.
 *
 * @type {string}
 */
export const SEARCH_LABEL =
  'Search submissions by claimant name or identifier, or by confirmation number';

/**
 * The search box above the combined history, controlled by
 * `CombinedHistory`, which keeps the text in its state and out of the URL,
 * since it names a claimant or an identifier. The list narrows when the
 * Search button or Enter applies the text. Typing alone changes nothing, so
 * the button does what it says, the summary's announcement is not cut off
 * mid-sentence by the next keystroke, and the search applies the way the
 * dialog's filters do. The box's own clear control empties it and applies
 * that, which restores the full list. Every filter lives in the Filters
 * dialog, `CombinedHistoryFiltersModal`, so a chosen value is never mistaken
 * for one of the portal's sort selects, which sit where a lone select would.
 *
 * The box reports no analytics, since the design system's va-search-input
 * would send what was typed, a claimant's name or an identifier, to the data
 * layer on each blur and search.
 *
 * The parent mounts this component again, with a new `key`, when the
 * no-match message's Clear is used, which empties the box.
 *
 * @param {Object} props
 * @param {string} props.query The search text in force.
 * @param {(change: { query: string }) => void} props.onChange Called with
 *   the text to apply. The parent ignores the text in force and holds a
 *   search applied during a reload until the reload ends.
 * @returns {JSX.Element}
 */
const CombinedHistoryFilters = ({ query, onChange }) => {
  // The box starts from the parent's text and keeps its own from there, as
  // va-search-input ignores a new `value` once typed into. The one outside
  // change, Clear, mounts this component again.
  const [typed, setTyped] = useState(query);

  // Typing only follows the box. The box's clear control reports an empty
  // input with no submit, and that is a search to apply.
  const handleInput = event => {
    const { value } = event.target;
    setTyped(value);
    if (value === '') onChange({ query: '' });
  };

  const handleSubmit = event => {
    event.preventDefault();
    const value = event.target.value ?? typed;
    setTyped(value);
    onChange({ query: value });
  };

  return (
    <div
      className="submissions__history-filters vads-u-margin-bottom--2"
      data-testid="combined-history-filters"
    >
      {/* The same words are the input's own label for a screen reader, so
          this copy is for sighted readers only. */}
      <p
        className="vads-u-margin-top--0 vads-u-margin-bottom--1"
        data-testid="combined-history-search-label"
        aria-hidden="true"
      >
        {SEARCH_LABEL}
      </p>
      <VaSearchInput
        class="submissions__history-search"
        data-testid="combined-history-search"
        label={SEARCH_LABEL}
        buttonText="Search"
        small
        disableAnalytics
        value={typed}
        onInput={handleInput}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

CombinedHistoryFilters.propTypes = {
  query: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CombinedHistoryFilters;
